import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import marketDataService from '../services/marketDataService';

const MarketOverview = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [marketData, setMarketData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerChildren = {
    visible: { 
      transition: { 
        staggerChildren: 0.2 
      } 
    }
  };

  const floatAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // Fetch market data
  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch data based on active category
        let data;
        switch (activeCategory) {
          case 'stocks':
            data = await marketDataService.fetchTopStocks();
            break;
          case 'crypto':
            data = await marketDataService.fetchTopCryptocurrencies(8);
            break;
          case 'commodities':
            data = await marketDataService.fetchCommoditiesData();
            break;
          case 'indices':
            data = await marketDataService.fetchIndicesData();
            break;
          case 'currencies':
            data = await marketDataService.fetchCurrencyData();
            break;
          default: // 'all'
            // Fetch a mix of different market data
            const [stocks, crypto, commodities] = await Promise.all([
              marketDataService.fetchTopStocks(),
              marketDataService.fetchTopCryptocurrencies(4),
              marketDataService.fetchCommoditiesData()
            ]);
            
            // Combine and limit to 8 items
            data = [...stocks.slice(0, 4), ...crypto.slice(0, 4)];
        }
        
        setMarketData(data);
      } catch (err) {
        console.error('Error fetching market data:', err);
        setError('Failed to load market data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (inView) {
      fetchMarketData();
    }
  }, [activeCategory, inView]);

  // Handle category change
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
  };

  // Render market cards based on data
  const renderMarketCards = () => {
    if (loading) {
      return (
        <div className="market-grid">
          {[...Array(8)].map((_, index) => (
            <motion.div 
              key={index}
              className="market-card loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="market-card-header">
                <div className="market-icon-placeholder"></div>
                <div className="market-info-placeholder">
                  <div className="symbol-placeholder"></div>
                  <div className="name-placeholder"></div>
                </div>
              </div>
              <div className="price-placeholder"></div>
              <div className="change-placeholder"></div>
            </motion.div>
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="market-error">
          <p>{error}</p>
          <button className="btn btn-outline" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      );
    }

    if (!marketData || marketData.length === 0) {
      return (
        <div className="market-empty">
          <p>No market data available</p>
        </div>
      );
    }

    return (
      <motion.div 
        className="market-grid staggered-animations"
        initial="hidden"
        animate="visible"
        variants={staggerChildren}
      >
        {marketData.map((item, index) => (
          <motion.div 
            key={`${item.symbol || item.id}-${index}`}
            className="market-card" 
            variants={fadeInUp}
            whileHover={{ 
              y: -10,
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)",
              borderColor: "#D4AF37",
              transition: { duration: 0.3 }
            }}
          >
            <div className="market-card-header">
              <motion.div 
                className="market-icon"
                animate={floatAnimation}
                transition={{ delay: index * 0.1 }}
              >
                {item.symbol?.includes('BTC') || item.id === 'bitcoin' ? '₿' : 
                 item.symbol?.includes('ETH') || item.id === 'ethereum' ? 'Ξ' : 
                 item.symbol?.includes('SOL') || item.id === 'solana' ? 'SOL' : 
                 item.symbol?.includes('AAPL') ? '📱' : 
                 item.symbol?.includes('MSFT') ? '💻' : 
                 item.symbol?.includes('GOOGL') ? '🔍' : 
                 item.symbol?.includes('AMZN') ? '🛒' : 
                 '📊'}
              </motion.div>
              <div className="market-info">
                <h3>{item.symbol || item.name}</h3>
                <p className="full-name">{item.name || (item.symbol === 'XAU' ? 'Gold' : item.symbol === 'XAG' ? 'Silver' : item.symbol)}</p>
              </div>
            </div>
            <div className="market-card-price">
              {typeof item.price === 'number' ? 
                (item.price >= 1 ? `${item.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 
                 `${item.price.toFixed(4)}`) : 
                'N/A'}
            </div>
            <div className={`market-card-change ${item.changePercent >= 0 ? 'positive' : 'negative'}`}>
              {item.changePercent >= 0 ? '▲' : '▼'} {Math.abs(item.changePercent).toFixed(2)}%
            </div>
          </motion.div>
        ))}
      </motion.div>
    );
  };

  return (
    <div className="market-overview-section">
      <div className="container">
        <motion.div 
          className="section-header"
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={fadeInUp}
          ref={ref}
        >
          <motion.h2
            animate={floatAnimation}
          >
            Live Market Overview
          </motion.h2>
          <p>Real-time prices and trends across global markets</p>
        </motion.div>
        
        <motion.div 
          className="market-categories"
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <motion.button 
            className={`category-btn ${activeCategory === 'all' ? 'active' : ''}`}
            whileHover={{ 
              scale: 1.05,
              backgroundColor: "#121F38",
              borderColor: "#D4AF37",
              transition: { duration: 0.3 }
            }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleCategoryChange('all')}
          >
            All Markets
          </motion.button>
          <motion.button 
            className={`category-btn ${activeCategory === 'stocks' ? 'active' : ''}`}
            whileHover={{ 
              scale: 1.05,
              backgroundColor: "#121F38",
              borderColor: "#D4AF37",
              transition: { duration: 0.3 }
            }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleCategoryChange('stocks')}
          >
            Stocks
          </motion.button>
          <motion.button 
            className={`category-btn ${activeCategory === 'crypto' ? 'active' : ''}`}
            whileHover={{ 
              scale: 1.05,
              backgroundColor: "#121F38",
              borderColor: "#D4AF37",
              transition: { duration: 0.3 }
            }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleCategoryChange('crypto')}
          >
            Cryptocurrencies
          </motion.button>
          <motion.button 
            className={`category-btn ${activeCategory === 'commodities' ? 'active' : ''}`}
            whileHover={{ 
              scale: 1.05,
              backgroundColor: "#121F38",
              borderColor: "#D4AF37",
              transition: { duration: 0.3 }
            }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleCategoryChange('commodities')}
          >
            Commodities
          </motion.button>
          <motion.button 
            className={`category-btn ${activeCategory === 'indices' ? 'active' : ''}`}
            whileHover={{ 
              scale: 1.05,
              backgroundColor: "#121F38",
              borderColor: "#D4AF37",
              transition: { duration: 0.3 }
            }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleCategoryChange('indices')}
          >
            Indices
          </motion.button>
          <motion.button 
            className={`category-btn ${activeCategory === 'currencies' ? 'active' : ''}`}
            whileHover={{ 
              scale: 1.05,
              backgroundColor: "#121F38",
              borderColor: "#D4AF37",
              transition: { duration: 0.3 }
            }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleCategoryChange('currencies')}
          >
            Currencies
          </motion.button>
        </motion.div>
        
        {renderMarketCards()}
      </div>
    </div>
  );
};

export default MarketOverview;